/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { Autocomplete, autocompleteClasses, useTheme } from "@mui/material";
import { translationKeys, useTranslation } from "../../../Internationalisierung/i18nBasis";
import AnyUtil from "../../../Utils/AnyUtil";
import getRandomId from "../../../Utils/getRandomId";
import StringUtil from "../../../Utils/StringUtil";
import useErrors from "../../../Utils/userErrors";
import withTooltip from "../../HOC/withTooltip";
import AutocompleteRenderInput from "../Components/AutocompleteRenderInput";
import useOptionsQuery from "../useOptionsQuery";
import AutocompleteSelectProps from "./AutocompleteSelectProps";

export default withTooltip(function AutocompleteSelect<TValue>(props: AutocompleteSelectProps<TValue>) {
    const { error, errorTexts, clearErrorsOnChange } = useErrors(props);
    const { t } = useTranslation();
    const { options, execute, executeDebounced } = useOptionsQuery(props.executeQuery);
    const theme = useTheme();

    return (
        <Autocomplete
            ListboxProps={{ className: "lan-autocompleteselect-clarity" }}
            autoComplete
            autoHighlight
            forcePopupIcon
            freeSolo={props.freeSolo ?? false}
            value={props.value}
            inputValue={props.inputValue}
            onChange={clearErrorsOnChange((e, value, reason, details) => {
                props.onChange(value as TValue | null);
            })}
            onInputChange={(e, value, reason) => {
                if (StringUtil.isEmpty(value) && props.nullValueOnEmptyInputValue) {
                    props.onChange(null);
                }

                if (!!props.onInputChange) {
                    props.onInputChange(value);
                }

                executeDebounced(value);
            }}
            onOpen={(e) => {
                // Initial alle Optionen laden, wenn das Popup aufgeklickt wird
                if (!options.loading) {
                    execute("");
                }
            }}
            options={options.loading ? [] : options.value}
            filterOptions={props.filterOptions ? props.filterOptions : (options, filterOptionsState) => {
                // Wir liefern hier einfach die Optionen. Diese werden ja bereits vom Server
                // gefiltert geliefert. Dies macht die Eingabe performanter und es kommt zu keinem
                // Flackern des Popups.
                return options;
            }}
            isOptionEqualToValue={props.getOptionSelected}
            getOptionLabel={(option) => props.getOptionLabel(option as TValue)}
            loading={options.loading}
            onBlur={props.onBlur}
            disabled={props.readonly}
            disableClearable={props.disableClearable ?? true}
            {...(!AnyUtil.isUndefined(props.renderOption) ? {
                renderOption: (liParams, option, renderOptionState) => (
                    <li style={{ borderBottom: props.showDivider ? `1px solid ${theme.palette.divider}` : "" }} {...liParams} key={getRandomId()}>
                        {props.renderOption!(option)}
                    </li>
                )
            } : {})}
            loadingText={t(translationKeys.autoComplete.laden)}
            clearText={t(translationKeys.autoComplete.leeren)}
            closeText={t(translationKeys.autoComplete.schliessen)}
            openText={t(translationKeys.autoComplete.oeffnen)}
            noOptionsText={t(translationKeys.autoComplete.keineOptionen)}
            renderTags={props.renderTags}
            sx={{
                [`& ${autocompleteClasses.popupIndicator}, & ${autocompleteClasses.clearIndicator}`]: {
                    padding: "4px !important", // korrigiert Wechselwirkung mit IconButton auf selber View
                },
            }}
            getOptionDisabled={option => props.getOptionDisabled?.(option as TValue) ?? false}
            renderInput={params => (
                <AutocompleteRenderInput
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        ...props.InputProps,
                        endAdornment: (
                            <>
                                {params.InputProps.endAdornment}
                                {props.InputProps?.endAdornment}
                            </>
                        ),
                    }}
                    loading={options.loading}
                    caption={props.caption}
                    required={props.required}
                    autoFocus={props.autoFocus}
                    disabled={!!props.readonly}
                    placeholder={props.placeholder}
                    error={error}
                    errors={errorTexts}
                />
            )}
        />
    );
});